import React, { useState } from 'react';
import { X, UploadCloud, CheckCircle, FileText, AlertCircle } from 'lucide-react';
import { DatePicker } from '../DatePicker';
import SearchableDropdown from '../SearchableDropdown';
import {
  staffDocumentRequests,
  staffDocumentUploads,
  formatFileSize,
} from '../../services/staffDocumentService';

import { RubyGemMark } from '../RubyGemMark';
// Port of platform-app components/team/OnBehalfModal.tsx for the tenant-less
// staff-service (RUBY light chrome). `tenantId` is accepted for prop-contract
// compatibility but ignored.

// FormField shape mirrors staffDocumentService.FormField.
interface OnBehalfFormField {
  id: string;
  label: string;
  type: string; // 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'checkbox' | ...
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

interface OnBehalfRequest {
  id: string;
  templateName: string;
  templateType?: string;
  documentTemplateId?: string;
  userId: string;
  formFields?: any[];
}

interface OnBehalfModalProps {
  /** Ignored (tenant-less staff-service); kept so page ports stay mechanical. */
  tenantId?: string;
  request: OnBehalfRequest;
  employeeName: string;
  mode: 'complete' | 'attach';
  onDone: () => void;
  onClose: () => void;
}

const inputClass =
  'w-full px-3 py-2 border border-stone-300 rounded-lg text-sm text-stone-900 bg-white placeholder-stone-400 focus:outline-none focus:border-admin-primary';

export default function OnBehalfModal({
  request,
  employeeName,
  mode,
  onDone,
  onClose,
}: OnBehalfModalProps) {
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve which branch to render. mode === 'attach' always uses the file path.
  const rawType = (request.templateType || '').toLowerCase();
  const branch: 'form' | 'upload' | 'acknowledgment' =
    mode === 'attach'
      ? 'upload'
      : rawType === 'form'
      ? 'form'
      : rawType === 'acknowledgment'
      ? 'acknowledgment'
      : 'upload'; // 'upload', 'fillable_doc', missing, or unknown all fall back to upload

  const setResponse = (id: string, value: any) =>
    setResponses((prev) => ({ ...prev, [id]: value }));

  // Upload helper: presign → S3 PUT. Returns the S3 key for the request body.
  const uploadToS3 = async (f: File): Promise<string> => {
    const contentType = f.type || 'application/octet-stream';
    const { uploadUrl, key } = await staffDocumentUploads.getUploadUrl(
      f.name,
      contentType,
      request.userId,
    );
    await staffDocumentUploads.uploadFile(uploadUrl, f, contentType);
    return key;
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'attach') {
        if (!file) {
          setError('Please choose a file to attach.');
          setSubmitting(false);
          return;
        }
        const key = await uploadToS3(file);
        await staffDocumentRequests.attachResult(request.id, {
          fileKey: key,
          fileName: file.name,
          fileType: file.type || undefined,
          fileSize: file.size,
        });
      } else if (branch === 'form') {
        // Validate required fields
        const missing = (request.formFields || []).filter(
          (f: OnBehalfFormField) =>
            f.required &&
            (responses[f.id] === undefined ||
              responses[f.id] === '' ||
              responses[f.id] === false)
        );
        if (missing.length > 0) {
          setError(`Please complete: ${missing.map((f: OnBehalfFormField) => f.label).join(', ')}`);
          setSubmitting(false);
          return;
        }
        await staffDocumentRequests.submitOnBehalf(request.id, {
          method: 'form',
          responses,
        });
      } else if (branch === 'upload') {
        if (!file) {
          setError('Please choose a file to upload.');
          setSubmitting(false);
          return;
        }
        const key = await uploadToS3(file);
        await staffDocumentRequests.submitOnBehalf(request.id, {
          method: 'upload',
          fileKey: key,
          fileName: file.name,
          fileType: file.type || undefined,
          fileSize: file.size,
          note: note.trim() || undefined,
        });
      } else {
        // acknowledgment
        await staffDocumentRequests.submitOnBehalf(request.id, {
          method: 'acknowledge',
          note: note.trim() || undefined,
        });
      }
      onDone();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field: OnBehalfFormField) => {
    const ftype = (field.type || 'text').toLowerCase();
    const val = responses[field.id];

    if (ftype === 'select') {
      const options = (field.options || []).map((opt) => ({ value: opt, label: opt }));
      return (
        <SearchableDropdown
          options={options}
          value={(val as string) || null}
          onChange={(v) => setResponse(field.id, v || '')}
          placeholder={field.placeholder || 'Select an option...'}
          searchPlaceholder="Search..."
        />
      );
    }

    if (ftype === 'checkbox') {
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!val}
            onChange={(e) => setResponse(field.id, e.target.checked)}
            className="w-4 h-4 rounded accent-admin-primary"
          />
          <span className="text-sm text-stone-700">{field.placeholder || 'Yes'}</span>
        </label>
      );
    }

    if (ftype === 'date') {
      return (
        <DatePicker
          value={(val as string) || ''}
          onChange={(v) => setResponse(field.id, v)}
          placeholder={field.placeholder || 'Select date'}
        />
      );
    }

    if (ftype === 'textarea') {
      return (
        <textarea
          rows={3}
          placeholder={field.placeholder || ''}
          value={(val as string) || ''}
          onChange={(e) => setResponse(field.id, e.target.value)}
          className={`${inputClass} resize-none`}
        />
      );
    }

    const htmlType =
      ftype === 'number'
        ? 'number'
        : ftype === 'email'
        ? 'email'
        : ftype === 'phone'
        ? 'tel'
        : 'text';
    return (
      <input
        type={htmlType}
        placeholder={
          field.placeholder ||
          (ftype === 'email' ? 'email@example.com' : ftype === 'phone' ? '(555) 555-5555' : '')
        }
        value={(val as string) || ''}
        onChange={(e) => setResponse(field.id, e.target.value)}
        className={inputClass}
      />
    );
  };

  const Dropzone = (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        className={`flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl px-4 py-8 cursor-pointer transition-colors ${
          dragOver ? 'border-admin-primary bg-admin-primary/10' : 'border-stone-300 bg-stone-50 hover:bg-stone-100'
        }`}
      >
        {file ? (
          <>
            <FileText size={28} className="text-emerald-600" />
            <span className="text-sm font-medium text-stone-900">{file.name}</span>
            <span className="text-xs text-stone-500">{formatFileSize(file.size)}</span>
            <span className="text-xs text-admin-primary underline">Choose a different file</span>
          </>
        ) : (
          <>
            <UploadCloud size={28} className="text-stone-400" />
            <span className="text-sm font-medium text-stone-600">
              Drag &amp; drop a file, or click to browse
            </span>
            <span className="text-xs text-stone-400">PDF, JPG or PNG</span>
          </>
        )}
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setFile(f);
          }}
        />
      </label>
    </div>
  );

  const title = mode === 'attach' ? 'Attach Results' : 'Submit on Behalf';
  const subtitle =
    mode === 'attach'
      ? 'Upload background check or verification results.'
      : branch === 'form'
      ? 'Fill the form on the staff member’s behalf.'
      : branch === 'acknowledgment'
      ? 'Record that the staff member acknowledged in person.'
      : 'Upload a paper copy provided by the staff member.';

  const submitLabel = mode === 'attach' ? 'Attach Results' : 'Record on behalf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-stone-200 flex items-start justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-stone-900">{title}</h3>
            <p className="text-sm text-stone-500">{request.templateName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Attribution banner */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              Recorded by you, on behalf of <b>{employeeName}</b>. The staff member will be notified.
            </span>
          </div>

          <p className="text-xs text-stone-500">{subtitle}</p>

          {/* FORM branch */}
          {mode === 'complete' && branch === 'form' && (
            <>
              {(request.formFields || []).map((field: OnBehalfFormField) => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-600 ml-1">*</span>}
                  </label>
                  {renderFormField(field)}
                </div>
              ))}
              {(!request.formFields || request.formFields.length === 0) && (
                <div className="text-center py-8 text-stone-500">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No form fields configured for this step.</p>
                </div>
              )}
            </>
          )}

          {/* UPLOAD branch (complete upload/fillable_doc, or attach mode) */}
          {(branch === 'upload') && (
            <>
              {Dropzone}
              {mode === 'complete' && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Note <span className="text-stone-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. paper copy handed in person"
                    className={inputClass}
                  />
                </div>
              )}
            </>
          )}

          {/* ACKNOWLEDGMENT branch */}
          {mode === 'complete' && branch === 'acknowledgment' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-lg p-3 text-sm text-stone-700">
                <CheckCircle size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                <span>
                  Confirm that <b>{employeeName}</b> read and acknowledged this in person.
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Note <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. signed in person"
                  className={inputClass}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="flex items-center gap-1.5 text-sm text-red-600">
              <AlertCircle size={14} /> {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-stone-200 bg-stone-50 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-admin-primary hover:bg-admin-secondary text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitting ? (
              <>
                <RubyGemMark size={16} drawing color="currentColor" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                {submitLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
